import { useCallback, useRef, useState } from "react";

// Inspired by: https://github.com/irvanherz/use-awaitable-component

export interface AwaitableComponentData<T> {
  isActive: boolean;
  resolve?: (value: T | undefined) => void;
  reject?: (reason: unknown) => void;
}

const useAwaitableComponent = <T,>() => {
  const [data, setData] = useState<AwaitableComponentData<T>>({
    isActive: false,
  });

  const dataRef = useRef(data);
  dataRef.current = data;

  const start = useCallback<() => Promise<T | undefined>>(async () => {
    if (dataRef.current.isActive) {
      throw new Error("Awaitable component already active.");
    }
    return new Promise((resolve, reject) => {
      setData({ isActive: true, resolve, reject });
    });
  }, []);

  const handleResolve = useCallback(
    (val: T | undefined) => {
      if (!data.isActive) {
        throw new Error("Awaitable component is not active.");
      }
      data.resolve?.(val);
      setData({ isActive: false });
    },
    [data],
  );

  const handleReject = useCallback(
    (err: unknown) => {
      if (!data.isActive) {
        throw new Error("Awaitable component is not awaiting.");
      }
      if (err) {
        data.reject?.(err);
      } else {
        data.resolve?.(undefined);
      }
      setData({ isActive: false });
    },
    [data],
  );

  return {
    isActive: data.isActive,
    start,
    handleResolve,
    handleReject,
  } as const;
};

export default useAwaitableComponent;
